"use client";
import { useEffect } from "react";
import { useFirstInteraction } from "@/lib/useFirstInteraction";
import { pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";
import { isMaintenanceActive } from "@/components/maintenance/flag";

interface AdSenseScriptProps {
  client?: string;
  testMode?: boolean;
}

// Loads adsbygoogle.js as an async script with data-ad-client and lets the
// AdSense console-enabled Auto Ads initialize themselves. We inject from
// useEffect (not SSR) because AdSense rewrites <ins> elements in place — if
// that ran before React hydration we'd hit "server HTML didn't match the
// client".
//
// Injection is deferred until the first real interaction (see
// useFirstInteraction): the loader sets third-party cookies as soon as it
// runs, so loading it eagerly fails Lighthouse's third-party-cookies / Best
// Practices audits and lengthens the critical path. An engaged visitor
// triggers it within the first tap/scroll; bounce visitors and synthetic
// loads never pay for it.
//
// We deliberately do NOT push `enable_page_level_ads: true` on first load.
// The data-ad-client attribute already triggers Auto Ads when the AdSense
// console has them enabled; an extra push re-evaluates layout AFTER the
// script's own init, which is the most likely cause of side rails
// flickering in/out on scroll. Every navigation is a full page load, so
// Auto Ads re-initialise naturally on each page — no SPA re-trigger needed.
// When Chrome's Heavy Ad Intervention fires it navigates the offending
// cross-origin ad iframe to a same-origin error page ("This ad used too
// many resources for your device, so Chrome removed it"). The slot keeps
// its rendered height, so the message sits visibly on the page. There's
// no per-slot API for this, but Chrome emits an `intervention` Report we
// can subscribe to and then find the affected slot heuristically: real ad
// iframes are cross-origin (contentDocument throws), the intervention
// iframe is same-origin (contentDocument is readable). Hide that slot's
// container so the user sees the page close up instead of the warning.
function hideInterventionSlots() {
  const slots = document.querySelectorAll<HTMLElement>("ins.adsbygoogle");
  for (const slot of slots) {
    for (const iframe of slot.querySelectorAll("iframe")) {
      try {
        if (iframe.contentDocument) {
          const container = (slot.parentElement as HTMLElement | null) ?? slot;
          container.style.display = "none";
          break;
        }
      } catch {
        // Cross-origin = a normal ad iframe; leave it alone.
      }
    }
  }
}

// No nonce: the CSP allowlists pagead2.googlesyndication.com by origin rather
// than trusting this injected script transitively via 'strict-dynamic'. See
// proxy.ts — dropping the nonce is what restored static rendering site-wide.
export function AdSenseScript({ client, testMode = false }: AdSenseScriptProps) {
  const ready = useFirstInteraction();
  useEffect(() => {
    if (!ready || !client) return;
    // Never load the loader on a maintenance render. data-ad-client triggers
    // Auto Ads autonomously, and Google-served ads on a page with no publisher
    // content is the actual AdSense policy exposure here. Manual AdSlots are
    // already gone — the page renders MaintenanceScreen instead of its content.
    if (isMaintenanceActive()) return;
    const id = "adsbygoogle-loader";
    if (document.getElementById(id)) return;

    const s = document.createElement("script");
    s.id = id;
    s.async = true;
    s.crossOrigin = "anonymous";
    s.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";
    s.setAttribute("data-ad-client", client);
    if (testMode) s.setAttribute("data-adtest", "on");
    document.head.appendChild(s);

    type ReportingObserverCtor = new (
      cb: (
        reports: Array<{ type: string; body?: { id?: string; message?: string } | null }>,
      ) => void,
      opts?: { types?: string[]; buffered?: boolean },
    ) => { observe: () => void; disconnect: () => void };
    const RO = (window as unknown as { ReportingObserver?: ReportingObserverCtor })
      .ReportingObserver;
    if (!RO) return;
    const observer = new RO(
      (reports) => {
        for (const r of reports) {
          if (r.type === "intervention" && /HeavyAd/i.test(r.body?.id ?? r.body?.message ?? "")) {
            pushEvent(analyticsEvents.ad_intervention, {
              id: r.body?.id,
              message: r.body?.message?.slice(0, 200),
            });
            hideInterventionSlots();
          }
        }
      },
      { types: ["intervention"], buffered: true },
    );
    observer.observe();
    return () => observer.disconnect();
  }, [ready, client, testMode]);

  return null;
}
